import { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Pagination } from '@/shared/components/pagination'
import { PageLayout } from '@/layouts/page-layout'
import { Button, Input, Chip, Spinner, Select, Option, Tooltip, IconButton } from '@material-tailwind/react'
import { OfficeSelector, CompanySelector } from '@/shared/selectors'
import { FiMessageSquare, FiFileText, FiArrowLeft } from 'react-icons/fi'
import { Link, useNavigate } from 'react-router-dom'
import { AccessLogDetailsModal, AccessLogObservationsModal } from './access-log-modals'
import moment from 'moment'
import { GunIcon } from '@/pages/access-control/access-form/gun-icon'
import { useAuthorization } from '@/hooks'
import { useSelector } from 'react-redux'

const Badge = ({ ok, eventType }) => {
  if (eventType === 'exit') return null
  return <Chip value={ok ? 'Autorizado' : 'No Autorizado'} color={ok ? 'green' : 'red'} size="sm" className="w-fit" />
}

const normalizeLog = (r) => ({
  id: r.id,
  account_scoped_id: r.account_scoped_id ?? r.accountScopedId,
  timestamp: r.timestamp,
  event_type: r.event_type ?? r.eventType,
  authorized: r.authorized,
  observations: r.observations,
  query_text: r.query_text ?? r.queryText,
  validation_result: r.validation_result ?? r.validationResult,
  person_snapshot: r.person_snapshot ?? r.personSnapshot,
  vehicle_snapshot: r.vehicle_snapshot ?? r.vehicleSnapshot,
  performed_by_snapshot: r.performed_by_snapshot ?? r.performedBySnapshot,
  office_name: r.office_name ?? r.officeName,
  company_name: r.company_name ?? r.companyName ?? r.person_snapshot?.company_name ?? r.personSnapshot?.company_name
})

const looksLikeRut = (s) => {
  if (!s) return false
  const cleaned = String(s).toUpperCase().replace(/[^0-9K]/g, '')
  return /^[0-9]{7,8}[0-9K]$/.test(cleaned)
}

const formatRut = (s) => {
  const cleaned = String(s).toUpperCase().replace(/[^0-9K]/g, '')
  if (cleaned.length < 2) return s
  const body = cleaned.slice(0, -1)
  const dv = cleaned.slice(-1)
  return `${body}-${dv}`
}

const isPistol = (row) => {
  const verifs = row?.validation_result?.verifications
  if (!Array.isArray(verifs)) return false
  return verifs.some(v => v && v.pistol === true)
}

const normalizeRestrictedOfficeIds = (user) => {
  const rawIds = user?.access_history_office_ids ?? user?.accessHistoryOfficeIds
  if (rawIds == null || !Array.isArray(rawIds)) return null

  return [...new Set(rawIds.map((id) => String(id)))]
}

export function AccessLogsPage() {
  const navigate = useNavigate()
  const currentUser = useSelector((state) => state.user.current)
  const { hasAccess } = useAuthorization({ redirection: { enable: false } })

  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({ page: 1, pages: 1, count: 0 })
  const [loading, setLoading] = useState(false)
  const [canViewHistory, setCanViewHistory] = useState(null)
  const [q, setQ] = useState('')
  const [eventType, setEventType] = useState('')
  const [authorized, setAuthorized] = useState('')
  const [officeId, setOfficeId] = useState(null)
  const [companyId, setCompanyId] = useState(null)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [order, setOrder] = useState({ field: 'timestamp', sort: 'desc' })
  const [authorizedOnlyReasons, setAuthorizedOnlyReasons] = useState(false)
  const [openDetails, setOpenDetails] = useState(null)
  const [openObs, setOpenObs] = useState(null)
  const restrictedOfficeIds = useMemo(() => normalizeRestrictedOfficeIds(currentUser), [currentUser])
  const isOfficeScopeRestricted = Array.isArray(restrictedOfficeIds)
  const hasSingleVisibleOffice = isOfficeScopeRestricted && restrictedOfficeIds.length === 1

  useEffect(() => {
    hasAccess('access#history')
      .then(setCanViewHistory)
      .catch(() => setCanViewHistory(false))
  }, [hasAccess])

  useEffect(() => {
    if (canViewHistory === false) {
      navigate('/controldeacceso', { replace: true })
    }
  }, [canViewHistory, navigate])

  useEffect(() => {
    if (!Array.isArray(restrictedOfficeIds)) return

    if (restrictedOfficeIds.length === 0) {
      setOfficeId(null)
      return
    }

    if (restrictedOfficeIds.length === 1) {
      setOfficeId(restrictedOfficeIds[0])
      return
    }

    setOfficeId((prev) => (
      prev && restrictedOfficeIds.includes(String(prev)) ? prev : null
    ))
  }, [restrictedOfficeIds])

  const onPageChange = ({ selected }) => {
    setMeta((prev) => ({ ...prev, page: (selected ?? 0) + 1 }))
  }

  const params = useMemo(() => {
    const p = {
      page: meta.page || 1,
      per_page: 20,
      order,
      range: { from, to }
    }
    if (q) p.q = q
    if (eventType) p.event_type = eventType
    if (authorized !== '') p.authorized = authorized
    if (officeId) p.office_id = officeId
    if (companyId) p.company_id = companyId
    if (authorizedOnlyReasons) p.authorized_only_reasons = true
    return p
  }, [q, eventType, authorized, officeId, companyId, from, to, order, authorizedOnlyReasons, meta.page])

  const fetchData = async () => {
    if (!canViewHistory) return

    setLoading(true)
    const { data } = await axios.get('/api/v1/access_control_logs', { params })

    const toArray = (d) => {
      if (Array.isArray(d)) return d
      if (Array.isArray(d?.data)) return d.data
      if (Array.isArray(d?.access_control_logs)) return d.access_control_logs
      if (Array.isArray(d?.records)) return d.records
      return []
    }

    const rowsArr = toArray(data).map(normalizeLog)
    setRows(rowsArr)

    const nextMeta =
      (data?.meta?.pagy) ? data.meta.pagy :
      (data?.meta) ? data.meta :
      { page: 1, pages: 1, count: rowsArr.length }

    setMeta(nextMeta)
    setLoading(false)
  }

  useEffect(() => {
    if (canViewHistory) fetchData()
  }, [canViewHistory, params]) // eslint-disable-line

  const downloadCSV = () => {
    const url = '/api/v1/access_control_logs/export'
    const qp = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (typeof v === 'object') {
        Object.entries(v).forEach(([ik, iv]) => qp.append(`${k}[${ik}]`, iv))
      } else {
        qp.append(k, v)
      }
    })
    window.location.href = `${url}?${qp.toString()}`
  }

  const toggleSort = (field) => {
    setOrder((prev) => ({
      field,
      sort: prev.field === field ? (prev.sort === 'asc' ? 'desc' : 'asc') : 'asc'
    }))
  }

  if (canViewHistory === null) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Spinner className="h-10 w-10" />
      </div>
    )
  }

  if (!canViewHistory) return null

  return (
    <PageLayout title="Registros de Acceso"
        actions={
      <div className="flex gap-3">
        <Link to="/controldeacceso">
          <Button className="flex justify-center items-center gap-2">
            <FiArrowLeft size="1rem" />
            Volver a Control de Acceso
          </Button>
        </Link>
      </div>
      }
    >
      <div className="flex flex-col gap-3 mb-4">
        {isOfficeScopeRestricted && (
          <div className="rounded-lg border border-blue-gray-100 bg-blue-gray-50 px-3 py-2 text-sm text-blue-gray-800">
            {restrictedOfficeIds.length === 0
              ? 'Tu usuario no tiene dependencias habilitadas para ver este historial.'
              : hasSingleVisibleOffice
                ? 'El historial está acotado automáticamente a tu dependencia habilitada.'
                : 'El historial está acotado automáticamente a tus dependencias habilitadas.'}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="sm:col-span-2">
              <Input
                label="Buscar (nombre, RUT, patente, consulta...)"
                value={q}
                onChange={e => setQ(e.target.value)}
              />
            </div>

            <Select
              label="Evento"
              value={eventType}
              onChange={val => setEventType(val || '')}
            >
              <Option value="">Todos</Option>
              <Option value="entry">Entrada</Option>
              <Option value="exit">Salida</Option>
              <Option value="query">Consulta</Option>
            </Select>

            <Select
              label="Autorizado"
              value={authorized}
              onChange={val => setAuthorized(val ?? '')}
            >
              <Option value="">Todos</Option>
              <Option value="true">Sí</Option>
              <Option value="false">No</Option>
            </Select>

            <div className="sm:col-span-2">
              <OfficeSelector
                label="Dependencia"
                value={officeId}
                onChange={(opt) => setOfficeId(opt?.value ? String(opt.value) : null)}
                isClearable={!hasSingleVisibleOffice}
                ids={restrictedOfficeIds}
                className="w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="sm:col-span-2">
              <CompanySelector
                label="Contratista"
                value={companyId}
                onChange={(opt) => setCompanyId(opt?.value ?? null)}
                isClearable
                className="w-full"
              />
            </div>

            <Input
              label="Desde"
              type="date"
              value={from}
              onChange={e => setFrom(e.target.value)}
            />
            <Input
              label="Hasta"
              type="date"
              value={to}
              onChange={e => setTo(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1" />
          <Button onClick={downloadCSV} variant="outlined">
            Exportar XLSX
          </Button>
        </div>
      </div>

      <div className="w-full overflow-auto rounded-xl border border-gray-200">
        <table className="min-w-[900px] w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <Th onClick={() => toggleSort('timestamp')}>Fecha y hora</Th>
              <Th>Evento</Th>
              <Th>Autorización</Th>
              <Th>Dependencia</Th>
              <Th>Persona</Th>
              <Th>RUT / Patente</Th>
              <Th>Contratista</Th>
              <Th>Detalles</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-6"><div className="flex items-center gap-2"><Spinner className="h-4 w-4" />Cargando...</div></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="p-6">Sin resultados</td></tr>
            ) : rows.map(r => {
              const pistol = isPistol(r)
              return (
                <tr key={r.id} className="border-t">
                  <Td>{moment(r.timestamp).format('YYYY-MM-DD HH:mm:ss')}</Td>
                  <Td>{{ query: 'Consulta', entry: 'Entrada', exit: 'Salida' }[r.event_type] || r.event_type}</Td>
                  <Td><Badge ok={r.authorized} eventType={r.event_type} /></Td>
                  <Td>{r.office_name || r.office?.name || '-'}</Td>
                  <Td>{r.person_snapshot?.full_name || '-'}</Td>
                  <Td>{
                    r.person_snapshot?.rut
                      || r.vehicle_snapshot?.patent
                      || (looksLikeRut(r.query_text) ? formatRut(r.query_text) : r.query_text)
                      || '-'
                  }</Td>
                  <Td>{r.company_name || '-'}</Td>
                  <Td>
                    <div className="flex items-center gap-1 justify-end">
                      {pistol && (
                        <Tooltip content="Leído con pistola">
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded text-gray-800">
                            <GunIcon className="h-4 w-4" />
                          </span>
                        </Tooltip>
                      )}

                      {r.observations ? (
                        <Tooltip content="Observaciones">
                          <IconButton variant="text" className="p-2" onClick={() => setOpenObs(r.observations)}>
                            <FiMessageSquare className="h-5 w-5" />
                          </IconButton>
                        </Tooltip>
                      ) : null}

                      <Tooltip content="Detalles">
                        <IconButton variant="text" className="p-2" onClick={() => setOpenDetails(r)}>
                          <FiFileText className="h-5 w-5" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center items-end mt-4">
        <Pagination
          onPageChange={onPageChange}
          page={(meta.page || 1) - 1}
          totalPage={meta.pages || 1}
        />
      </div>

      <AccessLogDetailsModal
        open={!!openDetails}
        onClose={() => setOpenDetails(null)}
        row={openDetails}
      />

      <AccessLogObservationsModal
        open={!!openObs}
        onClose={() => setOpenObs(null)}
        observations={openObs}
      />
    </PageLayout>
  )
}

const Th = ({ children, onClick }) => (
  <th onClick={onClick} className={`p-3 font-medium ${onClick ? 'cursor-pointer hover:underline' : ''}`}>{children}</th>
)

const Td = ({ children }) => <td className="p-3 align-top">{children}</td>
